import { CopyIcon, InfoCircledIcon, UpdateIcon } from '@radix-ui/react-icons';
import { Box, Flex, Grid, Strong, Text } from '@radix-ui/themes';
import { AimingContextHelper } from 'classes/helpers/aiming-context.helper';
import { NotifyHelper } from 'classes/helpers/notify.helper';
import { PitchDesignHelper } from 'classes/helpers/pitch-design.helper';
import { BreakView } from 'components/common/break-view';
import { CommonCallout } from 'components/common/callouts';
import { CommonDialog } from 'components/common/dialogs';
import { ErrorBoundary } from 'components/common/error-boundary';
import { CommonTextInput } from 'components/common/form/text';
import { CommonTooltip } from 'components/common/tooltip';
import usePitchListStore from 'components/sections/pitch-list/store/use-pitch-list-store';
import { MachineContext } from 'contexts/machine.context';
import { t } from 'i18next';
import { BallHelper } from 'lib_ts/classes/ball.helper';
import { MachineHelper } from 'lib_ts/classes/machine.helper';
import { FT_TO_INCHES } from 'lib_ts/classes/math.utilities';
import { MiscHelper } from 'lib_ts/classes/misc.helper';
import { TrajHelper } from 'lib_ts/classes/trajectory.helper';
import { BuildPriority } from 'lib_ts/enums/pitches.enums';
import { RADIX } from 'lib_ts/enums/radix-ui';
import {
  IBallState,
  IClosedLoopBuildChars,
  IPitch,
  ITrajectory,
  ITrajektRefBreak,
} from 'lib_ts/interfaces/pitches';
import { clone, first } from 'lodash';
import { useCallback, useContext, useEffect, useMemo, useState } from 'react';
import { PitchListsService } from 'services/pitch-lists.service';
import { PitchesService } from 'services/pitches.service';
import { StateTransformService } from 'services/state-transform.service';
import useListLibraryStore from 'stores/use-list-library-store';
import { useTrainedMsStore } from 'stores/use-trained-ms-store';

const COMPONENT_NAME = 'EditBreaksDialog';

// how many decimals to print
const PRECISION = 1;

// show warning if either x or z requested differs from corresponding avgs by more than this
const WARNING_THRESHOLD_INCHES = 10;

interface IProps {
  pitch: IPitch;

  // hides the update button
  readonly: boolean;
  onClose: () => void;
}

interface IState {
  actual_x_text?: string;
  actual_x_in: number;
  actual_z_text?: string;
  actual_z_in: number;

  target_x_text?: string;
  target_x_in: number;
  target_z_text?: string;
  target_z_in: number;

  overriding: boolean;
}

export const EditBreaksDialog = (props: IProps) => {
  const activeList = useListLibraryStore((s) => s.activeList);

  const loading = usePitchListStore((s) => s.loading);

  const { machine } = useContext(MachineContext);

  const [state, setState] = useState<IState>({
    actual_x_in: 0,
    actual_z_in: 0,
    target_x_in: -(BallHelper.getSafePitchBreaks(props.pitch)?.xInches ?? 0),
    target_z_in: BallHelper.getSafePitchBreaks(props.pitch)?.zInches ?? 0,
    overriding: false,
  });

  const trainingShots = useTrainedMsStore(
    (s) => s.trainingShotsDict[props.pitch.bs_hash] ?? []
  );

  // load shots
  useEffect(() => {
    useTrainedMsStore.getState().lazyLoadShots(props.pitch.bs_hash, false);
  }, [props.pitch.bs_hash]);

  const medians = useMemo(() => {
    if (trainingShots.length === 0) {
      return undefined;
    }

    const bss = trainingShots
      .filter((s) => !!s.bs)
      .map((s) => s.bs as IBallState);
    const breaks = trainingShots.map((s) => TrajHelper.getBreaksFromShot(s));
    const trajs = trainingShots
      .filter((s) => !!s.traj)
      .map((s) => s.traj as ITrajectory);

    return {
      bs:
        bss.length > 0
          ? (MiscHelper.getMedianObject(bss) as IBallState)
          : undefined,
      break:
        breaks.length > 0
          ? (MiscHelper.getMedianObject(breaks) as ITrajektRefBreak)
          : undefined,
      traj:
        trajs.length > 0
          ? (MiscHelper.getMedianObject(trajs) as ITrajectory)
          : undefined,
    };
  }, [trainingShots]);

  useEffect(() => {
    if (!medians?.break) {
      return;
    }

    const { pitch } = props;

    const target =
      pitch.priority === BuildPriority.Breaks
        ? (pitch.breaks ?? TrajHelper.getBreaks(pitch.traj))
        : undefined;

    // in hawkeye frame of ref
    const actual_x_in = -1 * medians.break.xInches;
    const actual_z_in = medians.break.zInches;

    // in hawkeye frame of ref
    const target_x_in = target ? -1 * target.xInches : actual_x_in;
    const target_z_in = target ? target.zInches : actual_z_in;

    setState((prev) => ({
      ...prev,
      actual_x_in: actual_x_in,
      actual_x_text: actual_x_in.toFixed(PRECISION),
      actual_z_in: actual_z_in,
      actual_z_text: actual_z_in.toFixed(PRECISION),

      target_x_in: target_x_in,
      target_x_text: target_x_in.toFixed(PRECISION),
      target_z_in: target_z_in,
      target_z_text: target_z_in.toFixed(PRECISION),
    }));
  }, [medians]);

  const submit = useCallback(
    async (mode: 'copy' | 'update') => {
      if (mode === 'update' && props.readonly) {
        NotifyHelper.error({
          message_md: 'Updating is not allowed in read-only mode.',
        });
        return;
      }

      if (!medians) {
        return;
      }

      if (!medians.break) {
        NotifyHelper.error({
          message_md: 'There is insufficient data to proceed.',
        });
        return;
      }

      if (!medians.traj) {
        NotifyHelper.error({
          message_md: 'There is insufficient data to proceed.',
        });
        return;
      }

      const tms = useTrainedMsStore.getState().getTms(props.pitch.bs_hash);

      if (!tms?.ms) {
        NotifyHelper.error({ message_md: 'There is no machine state.' });
        return;
      }

      const chars: IClosedLoopBuildChars = {
        machineID: tms.machineID,
        mongo_id: props.pitch._id,
        priority: BuildPriority.Breaks,
        use_gradient: true,
        ms: tms.ms,
        traj: medians.traj,
        seams: props.pitch.seams,
        target_bs: props.pitch.bs,
        actual_bs: medians.bs ?? props.pitch.bs,
        actual_mvmt: {
          break_x_ft: medians.break.xInches / FT_TO_INCHES,
          break_z_ft: medians.break.zInches / FT_TO_INCHES,
        },
        target_mvmt: {
          break_x_ft: (-1 * state.target_x_in) / FT_TO_INCHES,
          break_z_ft: state.target_z_in / FT_TO_INCHES,
        },
        override_mvmt: state.overriding
          ? {
              break_x_ft: (-1 * state.actual_x_in) / FT_TO_INCHES,
              break_z_ft: state.actual_z_in / FT_TO_INCHES,
            }
          : undefined,
      };

      const result = first(
        await StateTransformService.getInstance().buildManyClosedLoop({
          machineID: tms.machineID,
          pitches: [chars],
          stepSize: 1,
          notifyError: true,
        })
      );

      if (!result) {
        NotifyHelper.error({
          message_md: 'Failed to find adjusted pitch by ID.',
        });
        return;
      }

      const upsertedTms = await useTrainedMsStore.getState().upsertTms({
        machineID: tms.machineID,
        ball_type: tms.ball_type,
        bs_hash: MachineHelper.getBsHash(result.target_bs, chars.priority),
        ms: result.ms,
        shotIDs: [],
        contShotErrors: null,
        aimingTraj: result.traj,
        // * IMPORTANT: prevents PL from refreshing the ms before it can be trained
        skipRefresh: true,
        deleted: false,
      });

      if (!upsertedTms) {
        NotifyHelper.error({
          message_md: 'Failed to create optimized machine state.',
        });
        return;
      }

      const payload: Partial<IPitch> = clone(props.pitch);

      payload.bs = result.target_bs;
      payload.bs_hash = upsertedTms.bs_hash;
      payload.traj = result.traj;
      payload._mx_tms = upsertedTms;

      payload.seams = result.seams;
      payload.breaks = {
        xInches: -1 * state.target_x_in,
        zInches: state.target_z_in,
      };
      payload.priority = BuildPriority.Breaks;

      const aimed = AimingContextHelper.aimAdHoc({
        source: `${COMPONENT_NAME} > submit`,
        machine: machine,
        pitch: payload as IPitch,
        ms: upsertedTms.ms,
        plate: TrajHelper.getPlateLoc(props.pitch.traj),
        aimingShots: [],
      });

      if (!aimed) {
        NotifyHelper.error({ message_md: 'Failed to build new pitch.' });
        return;
      }

      aimed.pitch._original_id = props.pitch._id;

      // fix the prefix on the pitch name when copying (to disambiguate)
      if (mode === 'copy') {
        const nameSuffix = `(HB: ${state.target_x_in.toFixed(
          PRECISION
        )}, VB: ${state.target_z_in.toFixed(PRECISION)})`;
        aimed.pitch.name = `${props.pitch.name} ${nameSuffix}`;
      }

      // add the new pitch the list
      await PitchListsService.getInstance()
        .addPitches({
          listID: props.pitch._parent_id,
          data: [aimed.pitch],
        })
        .then((result) => {
          if (result.success) {
            return;
          }

          NotifyHelper.warning({
            message_md: result.error ?? 'Pitch could not be created.',
          });
        });

      // when updating, the new pitch has to replace the old version
      if (mode === 'update') {
        await PitchesService.getInstance().markAsDeleted([props.pitch._id]);
      }

      usePitchListStore.getState().reloadPitches({
        activeList: activeList,
      });

      props.onClose();
    },
    [
      props.readonly,
      props.pitch,
      props.onClose,
      machine,
      trainingShots,
      medians,
      state.target_x_in,
      state.target_z_in,
      state.overriding,
      state.actual_x_in,
      state.actual_z_in,
      activeList,
    ]
  );

  const renderWarning = useCallback(() => {
    const diffX = state.actual_x_in - state.target_x_in;
    const warnX = Math.abs(diffX) > WARNING_THRESHOLD_INCHES;

    const diffZ = state.actual_z_in - state.target_z_in;
    const warnZ = Math.abs(diffZ) > WARNING_THRESHOLD_INCHES;

    if (!warnX && !warnZ) {
      return null;
    }

    const msg = (() => {
      if (warnX && warnZ) {
        return t('pl.edit-breaks-both-differ-from-x', {
          x: WARNING_THRESHOLD_INCHES,
        }).toString();
      }

      if (warnX) {
        return t('pl.edit-breaks-h-differs-from-x', {
          x: WARNING_THRESHOLD_INCHES,
        }).toString();
      }

      return t('pl.edit-breaks-v-differs-from-x', {
        x: WARNING_THRESHOLD_INCHES,
      }).toString();
    })();

    return <CommonCallout text={msg} />;
  }, [
    state.actual_x_in,
    state.target_x_in,
    state.actual_z_in,
    state.target_z_in,
  ]);

  const pitchName = props.pitch.name;

  return (
    <ErrorBoundary identifier={COMPONENT_NAME}>
      <CommonDialog
        identifier={COMPONENT_NAME}
        title="pl.edit-breaks"
        subtitle={
          <Text size={RADIX.TEXT.SIZE.SM} color={RADIX.COLOR.SECONDARY}>
            {t('common.pitch')}: {pitchName}
          </Text>
        }
        width={RADIX.DIALOG.WIDTH.LG}
        onClose={props.onClose}
        buttons={[
          {
            label: t(
              state.overriding
                ? 'pl.edit-breaks-override-on'
                : 'pl.edit-breaks-override-off'
            ).toString(),
            color: state.overriding ? RADIX.COLOR.WARNING : undefined,
            invisible: trainingShots.length === 0,
            onClick: () => {
              setState((prev) => {
                if (!medians?.break) {
                  return prev;
                }

                const nextValue = clone(prev);

                // flip the switch
                nextValue.overriding = !nextValue.overriding;

                // reset the values
                nextValue.actual_x_in = -medians.break.xInches;
                nextValue.actual_x_text = (-medians.break.xInches).toFixed(
                  PRECISION
                );
                nextValue.actual_z_in = medians.break.zInches;
                nextValue.actual_z_text =
                  medians.break.zInches.toFixed(PRECISION);

                return nextValue;
              });
            },
          },
          {
            icon: <CopyIcon />,
            label: 'common.copy',
            color: RADIX.COLOR.SUCCESS,
            variant: 'solid',
            disabled: trainingShots.length === 0,
            invisible: trainingShots.length === 0,
            onClick: () => submit('copy'),
          },
          {
            icon: <UpdateIcon />,
            label: 'common.update',
            color: RADIX.COLOR.WARNING,
            invisible: trainingShots.length === 0 || props.readonly,
            onClick: () => submit('update'),
          },
        ]}
        content={
          <Flex gap={RADIX.FLEX.GAP.LG} direction="column">
            <Text>{t('pl.edit-breaks-msg')}</Text>

            {trainingShots.length === 0 && (
              <CommonCallout text="pl.edit-breaks-insufficient-data-msg" />
            )}

            <Flex style={{ width: '100%' }} justify="center" align="center">
              <Box style={{ height: '400px', width: '400px' }}>
                <BreakView
                  actual_x_in={state.actual_x_in}
                  actual_z_in={state.actual_z_in}
                  target_x_in={state.target_x_in}
                  target_z_in={state.target_z_in}
                  shots={trainingShots}
                  overriding={state.overriding}
                  onUpdate={(result) => {
                    setState((prev) => ({
                      ...prev,
                      target_x_text: result.xInches.toFixed(PRECISION),
                      target_x_in: result.xInches,
                      target_z_text: result.zInches.toFixed(PRECISION),
                      target_z_in: result.zInches,
                    }));
                  }}
                />
              </Box>
            </Flex>

            <Grid columns="3" gap={RADIX.FLEX.GAP.MD}>
              <Box>&nbsp;</Box>
              <Flex align="center" gap="1">
                <Text>
                  {t(
                    state.overriding ? 'common.user-override' : 'common.actual'
                  )}
                </Text>
                <CommonTooltip
                  trigger={<InfoCircledIcon />}
                  text={
                    state.overriding
                      ? 'pl.actual-break-from-user'
                      : 'pl.actual-break-from-device'
                  }
                />
              </Flex>
              <Box>
                <CommonTooltip
                  trigger={<Strong>{t('common.target')}</Strong>}
                  text_md={PitchDesignHelper.HB_TOOLTIP_TEXT}
                />
              </Box>
              <Box>{t('common.horizontal-break-in')}</Box>
              <Box>
                <CommonTextInput
                  id="edit-breaks-actual-x"
                  name="actual_x_text"
                  type="number"
                  className="align-center"
                  value={state.actual_x_text}
                  disabled={loading || !state.overriding}
                  onOptionalNumericChange={(v) => {
                    setState((prev) => ({
                      ...prev,
                      actual_x_text: v?.toString(),
                      actual_x_in: v ?? 0,
                    }));
                  }}
                />
              </Box>
              <Box>
                <CommonTextInput
                  id="edit-breaks-target-x"
                  name="target_x_text"
                  type="number"
                  className="align-center"
                  value={state.target_x_text}
                  disabled={loading}
                  onOptionalNumericChange={(v) => {
                    setState((prev) => ({
                      ...prev,
                      target_x_text: v?.toString(),
                      target_x_in: v ?? 0,
                    }));
                  }}
                />
              </Box>
              <Box>{t('common.vertical-break-in')}</Box>
              <Box>
                <CommonTextInput
                  id="edit-breaks-actual-z"
                  name="actual_z_text"
                  type="number"
                  className="align-center"
                  value={state.actual_z_text}
                  disabled={loading || !state.overriding}
                  onOptionalNumericChange={(v) => {
                    setState((prev) => ({
                      ...prev,
                      actual_z_text: v?.toString(),
                      actual_z_in: v ?? 0,
                    }));
                  }}
                />
              </Box>
              <Box>
                <CommonTextInput
                  id="edit-breaks-target-z"
                  name="target_z_text"
                  type="number"
                  className="align-center"
                  value={state.target_z_text}
                  disabled={loading}
                  onOptionalNumericChange={(e) => {
                    setState((prev) => ({
                      ...prev,
                      target_z_text: e?.toString(),
                      target_z_in: e ?? 0,
                    }));
                  }}
                />
              </Box>
            </Grid>

            {renderWarning()}
          </Flex>
        }
      />
    </ErrorBoundary>
  );
};
